import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  buildStaticNetwork,
  computeAggregates,
  defaultTrainTypeMeta,
  normalizeKey,
  resolveTrainTypeKeyFromName,
  trainTypeMeta,
} from "../data/staticNetwork";
import {
  batchRequest,
  getZoneData,
  loginWithToken,
  logout,
  searchUnits,
} from "../api/wialonClient";

const staticNetwork = buildStaticNetwork();
const routeSegments = staticNetwork.routes.flatMap((route) =>
  route.stations.slice(0, -1).map((fromStation, index) => ({
    routeId: route.id,
    routeName: route.name,
    routeColor: route.color,
    from: fromStation,
    to: route.stations[index + 1],
  }))
);
const routeById = new Map(staticNetwork.routes.map((route) => [route.id, route]));

const allStations = staticNetwork.routes.flatMap((route) => route.stations);
const DEFAULT_REFRESH_INTERVAL_MS = 5000;

const distanceSquared = (lat1, lng1, lat2, lng2) => {
  const dLat = lat1 - lat2;
  const dLng = lng1 - lng2;
  return dLat * dLat + dLng * dLng;
};

const computeSegmentMetrics = (lat, lng, from, to) => {
  if (!from || !to) {
    return null;
  }

  const ax = from.lat;
  const ay = from.lng;
  const bx = to.lat;
  const by = to.lng;

  if (!Number.isFinite(ax) || !Number.isFinite(ay) || !Number.isFinite(bx) || !Number.isFinite(by)) {
    return null;
  }

  const dx = bx - ax;
  const dy = by - ay;

  if (dx === 0 && dy === 0) {
    return { ratio: 0, distanceSquared: distanceSquared(lat, lng, ax, ay) };
  }

  const t = ((lat - ax) * dx + (lng - ay) * dy) / (dx * dx + dy * dy);
  const clamped = Math.max(0, Math.min(1, t));
  const projLat = ax + clamped * dx;
  const projLng = ay + clamped * dy;

  return {
    ratio: clamped,
    distanceSquared: distanceSquared(lat, lng, projLat, projLng),
  };
};

const findNearestSegment = (lat, lng) => {
  let best = null;

  for (const segment of routeSegments) {
    const metrics = computeSegmentMetrics(lat, lng, segment.from, segment.to);
    if (!metrics) {
      continue;
    }

    if (!best || metrics.distanceSquared < best.distanceSquared) {
      best = {
        ...segment,
        distanceSquared: metrics.distanceSquared,
        ratio: metrics.ratio,
      };
    }
  }

  return best;
};

const findNearestStation = (lat, lng) => {
  let best = null;

  for (const station of allStations) {
    if (!Number.isFinite(station.lat) || !Number.isFinite(station.lng)) {
      continue;
    }

    const dist = distanceSquared(lat, lng, station.lat, station.lng);

    if (!best || dist < best.distanceSquared) {
      const route = staticNetwork.routes.find((candidate) => candidate.id === station.routeId);
      const stations = route?.stations || [];
      const index = stations.findIndex((entry) => entry.id === station.id);

      best = {
        station,
        nextStation: index >= 0 && index + 1 < stations.length ? stations[index + 1] : null,
        previousStation: index > 0 ? stations[index - 1] : null,
        distanceSquared: dist,
      };
    }
  }

  return best;
};

const readQueryParamsFromLocation = () => {
  if (typeof window === "undefined" || !window.location) {
    return { sid: null, baseUrl: null };
  }

  const params = new URLSearchParams(window.location.search);
  const sid = params.get("sid");
  const baseUrl = params.get("baseUrl");

  return {
    sid: sid && sid.trim() ? sid : null,
    baseUrl: baseUrl && baseUrl.trim() ? baseUrl : null,
  };
};

const resolveNumber = (value) => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
};

const configuredZoneMapping = (() => {
  const mapping = new Map();
  const raw = process.env.REACT_APP_WIALON_ZONE_IDS;

  const addEntry = (key, values) => {
    if (!values) {
      return;
    }

    const source = Array.isArray(values) ? values : [values];
    const numericValues = source
      .map((item) => resolveNumber(item))
      .filter((item) => typeof item === "number");

    if (!numericValues.length) {
      return;
    }

    const existing = mapping.get(key) || [];
    const merged = new Set([...existing, ...numericValues]);
    mapping.set(key, Array.from(merged));
  };

  if (!raw) {
    return mapping;
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) || typeof parsed === "number") {
      addEntry("default", parsed);
    } else if (parsed && typeof parsed === "object") {
      Object.entries(parsed).forEach(([key, value]) => {
        addEntry(String(key).trim(), value);
      });
    }
  } catch (error) {
    const fallback = raw
      .split(/[^0-9.-]+/)
      .map((value) => resolveNumber(value))
      .filter((value) => typeof value === "number");
    addEntry("default", fallback);
  }

  return mapping;
})();

const readDefaultResourceId = () => {
  const envCandidates = [
    process.env.REACT_APP_WIALON_RESOURCE_ID,
    process.env.REACT_APP_WIALON_DEFAULT_RESOURCE_ID,
    process.env.REACT_APP_WIALON_RESOURCE,
  ];

  for (const candidate of envCandidates) {
    const numeric = resolveNumber(candidate);
    if (typeof numeric === "number") {
      return numeric;
    }
  }

  const configuredCandidates = Array.from(configuredZoneMapping.keys())
    .map((key) => resolveNumber(key))
    .filter((value) => typeof value === "number");

  if (configuredCandidates.length) {
    return configuredCandidates[0];
  }

  // Fallback to the legacy Baghdad Rail resource id so the polling payload
  // mirrors the original integration when no overrides are supplied.
  return 18636489;
};

const defaultResourceId = readDefaultResourceId();

// Wialon expects a "spec" object describing which geofences to evaluate when we
// call `resource/get_zones_by_point`. `buildZoneSpec` assembles that payload by
// combining every station id from the static network snapshot with any
// overrides supplied through environment configuration. The resulting shape is
// cached so we can reuse it for each batched lookup in `refresh()`.
const buildZoneSpec = (resourceId) => {
  const resolvedResourceId = resolveNumber(resourceId);

  if (typeof resolvedResourceId !== "number") {
    return null;
  }

  const zoneIds = new Set();

  staticNetwork.routes.forEach((route) => {
    route.stations.forEach((station) => {
      const numericId = resolveNumber(station.id);
      if (typeof numericId === "number") {
        zoneIds.add(numericId);
      }
    });
  });

  const configured =
    configuredZoneMapping.get(String(resolvedResourceId)) ||
    configuredZoneMapping.get(resolvedResourceId) ||
    configuredZoneMapping.get("default") ||
    configuredZoneMapping.get(null);

  if (configured) {
    configured.forEach((id) => {
      const numericId = resolveNumber(id);
      if (typeof numericId === "number") {
        zoneIds.add(numericId);
      }
    });
  }

  if (!zoneIds.size) {
    return null;
  }

  return {
    flags: 1,
    zoneId: {
      [resolvedResourceId]: Array.from(zoneIds).sort((a, b) => a - b),
    },
  };
};

const buildAllZonesSpec = (resourceId) => {
  const resolvedResourceId = resolveNumber(resourceId);

  if (typeof resolvedResourceId !== "number") {
    return null;
  }

  return {
    flags: 1,
    resourceId: resolvedResourceId,
    zoneId: {
      [resolvedResourceId]: [],
    },
  };
};

const normalizeGeofences = (zonesResponse, stationById, metadataById) => {
  if (!zonesResponse) {
    return [];
  }

  const annotateZone = (zone) => {
    const normalized = normalizeZoneRecord(zone, metadataById);
    if (!normalized) {
      return null;
    }

    const isStation =
      typeof normalized.id === "number" && stationById?.has(normalized.id);

    return {
      ...normalized,
      isStation: Boolean(isStation),
    };
  };

  if (Array.isArray(zonesResponse)) {
    return zonesResponse.map(annotateZone).filter(Boolean);
  }

  if (Array.isArray(zonesResponse.items)) {
    return zonesResponse.items.map(annotateZone).filter(Boolean);
  }

  if (zonesResponse.items && typeof zonesResponse.items === "object") {
    return Object.values(zonesResponse.items)
      .map(annotateZone)
      .filter(Boolean);
  }

  if (zonesResponse.zones && typeof zonesResponse.zones === "object") {
    return Object.values(zonesResponse.zones)
      .map(annotateZone)
      .filter(Boolean);
  }

  const fallback = [];

  const addFallbackRecord = (resourceIdCandidate, zoneIdCandidate) => {
    const zoneId = resolveNumber(zoneIdCandidate);
    if (typeof zoneId !== "number") {
      return;
    }

    const resourceId = resolveNumber(resourceIdCandidate);
    const station = stationById?.get(zoneId);
    const metadata =
      metadataById instanceof Map && metadataById.has(zoneId)
        ? metadataById.get(zoneId)
        : null;

    const derivedName = station?.name ?? metadata?.name;

    if (!derivedName) {
      return;
    }

    fallback.push({
      id: zoneId,
      name: derivedName,
      resourceId:
        typeof resourceId === "number"
          ? resourceId
          : metadata?.resourceId ?? station?.resourceId ?? null,
      type:
        typeof metadata?.type === "number" ? metadata.type : null,
      isStation: Boolean(station),
    });
  };

  if (zonesResponse && typeof zonesResponse === "object") {
    Object.entries(zonesResponse).forEach(([key, value]) => {
      if (key === "error" || key === "errorCode") {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((zoneId) => addFallbackRecord(key, zoneId));
        return;
      }

      if (value && typeof value === "object") {
        const nested = normalizeGeofences(value, stationById, metadataById);
        if (nested.length) {
          fallback.push(...nested);
        }
      }
    });
  }

  if (fallback.length) {
    return fallback;
  }

  return [];
};

const buildZoneMetadataMap = (response) => {
  const metadata = new Map();
  const visited = new Set();
  const queue = [];

  if (response !== undefined && response !== null) {
    queue.push(response);
  }

  while (queue.length) {
    const entry = queue.shift();

    if (!entry || typeof entry !== "object") {
      continue;
    }

    if (visited.has(entry)) {
      continue;
    }

    visited.add(entry);

    const zones = normalizeGeofences(entry, staticNetwork.stationById, null);

    zones.forEach((zone) => {
      if (zone && typeof zone.id === "number" && zone.name) {
        metadata.set(zone.id, zone);
      }
    });

    const nestedCandidates = [];

    if (entry.result && entry.result !== entry) {
      nestedCandidates.push(entry.result);
    }

    if (entry.items && entry.items !== entry) {
      nestedCandidates.push(entry.items);
    }

    if (entry.zones && entry.zones !== entry) {
      nestedCandidates.push(entry.zones);
    }

    nestedCandidates.forEach((candidate) => {
      if (candidate && typeof candidate === "object") {
        if (Array.isArray(candidate)) {
          candidate.forEach((value) => queue.push(value));
        } else {
          queue.push(candidate);
        }
      }
    });
  }

  return metadata;
};

const mergeGeofenceLists = (primary = [], secondary = []) => {
  if (!Array.isArray(primary) || !primary.length) {
    return Array.isArray(secondary) ? [...secondary] : [];
  }

  if (!Array.isArray(secondary) || !secondary.length) {
    return [...primary];
  }

  const result = [...primary];
  const seen = new Set();

  const markSeen = (zone) => {
    if (!zone) {
      return;
    }

    if (typeof zone.id === "number") {
      seen.add(`id:${zone.id}`);
    } else if (zone.name) {
      seen.add(`name:${normalizeKey(zone.name)}|${zone.resourceId ?? ""}`);
    }
  };

  result.forEach(markSeen);

  for (const zone of secondary) {
    if (!zone) {
      continue;
    }

    const key =
      typeof zone.id === "number"
        ? `id:${zone.id}`
        : zone.name
        ? `name:${normalizeKey(zone.name)}|${zone.resourceId ?? ""}`
        : null;

    if (key && seen.has(key)) {
      continue;
    }

    result.push(zone);

    if (key) {
      seen.add(key);
    }
  }

  return result;
};

const normalizeZoneRecord = (zone, metadataById) => {
  if (!zone) {
    return null;
  }

  const id = zone.id ?? zone.i ?? zone.zoneId;
  const numericId = resolveNumber(id);
  const resolvedId = typeof numericId === "number" ? numericId : id;
  const lookupKey = typeof numericId === "number" ? numericId : resolvedId;
  const metadata =
    metadataById instanceof Map && metadataById.has(lookupKey)
      ? metadataById.get(lookupKey)
      : undefined;

  const rawName = zone.name ?? zone.n ?? zone.zoneName ?? metadata?.name ?? null;
  const normalizedName = typeof rawName === "string" ? rawName.trim() : null;
  const resourceCandidate =
    zone.resourceId ?? zone.r ?? zone.res_id ?? metadata?.resourceId ?? null;
  const normalizedResourceId = resolveNumber(resourceCandidate);
  const rawType = zone.type ?? zone.t ?? zone.tp ?? metadata?.type ?? null;
  const normalizedType = resolveNumber(rawType);

  if (!normalizedName && !metadata?.name) {
    return null;
  }

  return {
    id: resolvedId,
    name: normalizedName || metadata?.name || null,
    resourceId:
      typeof normalizedResourceId === "number"
        ? normalizedResourceId
        : metadata?.resourceId ?? resourceCandidate ?? null,
    type:
      typeof normalizedType === "number"
        ? normalizedType
        : typeof metadata?.type === "number"
        ? metadata.type
        : null,
  };
};

const extractPosition = (unit) => {
  const candidate =
    unit?.pos || unit?.position || unit?.last_message?.pos || unit?.lmsg?.pos;

  if (!candidate) {
    return null;
  }

  const lat = candidate.y ?? candidate.lat ?? candidate.latitude;
  const lng = candidate.x ?? candidate.lon ?? candidate.longitude;

  if (typeof lat !== "number" || typeof lng !== "number") {
    return null;
  }

  const speed = candidate.s ?? candidate.speed ?? candidate.v;
  const timestamp = candidate.t ?? candidate.time;
  const course = candidate.c ?? candidate.course ?? candidate.dir ?? candidate.heading;

  return {
    lat,
    lng,
    speed: typeof speed === "number" ? speed : null,
    timestamp: typeof timestamp === "number" ? timestamp * 1000 : null,
    course: typeof course === "number" ? course : null,
  };
};

const resolveTrainTypeKey = (unit) => {
  const rawName =
    (typeof unit?.nm === "string" && unit.nm.trim()) ||
    (typeof unit?.name === "string" && unit.name.trim()) ||
    "";

  if (rawName) {
    return resolveTrainTypeKeyFromName(rawName);
  }

  const candidates = [
    unit?.prp?.train_type,
    unit?.prp?.type,
    unit?.ct,
    unit?.cls,
    unit?.hw?.type,
  ];

  for (const candidate of candidates) {
    const numeric = resolveNumber(candidate);
    if (typeof numeric === "number") {
      return numeric === 1 ? "passenger" : "freight";
    }
  }

  return "other";
};

const matchStationByZone = (zones, stationNameMap, stationById) => {
  for (const zone of zones) {
    if (zone.id && stationById?.has(zone.id)) {
      return stationById.get(zone.id);
    }

    const key = normalizeKey(zone.name);
    if (stationNameMap.has(key)) {
      return stationNameMap.get(key);
    }
  }

  return null;
};

const buildTrainFromUnit = ({
  unit,
  position,
  geofences,
  stationNameMap,
  stationById,
  lastStationId,
  lastStationMeta,
}) => {
  const station = matchStationByZone(geofences, stationNameMap, stationById);
  const typeId = resolveTrainTypeKey(unit);
  const baseType = trainTypeMeta[typeId] || defaultTrainTypeMeta();
  const type = {
    ...baseType,
    localizationKey: baseType.localizationKey || typeId,
  };
  let progress = null;
  let fromStation = station || null;
  let toStation = station || null;
  let routeColor = station?.routeColor ?? type.color;
  let routeId = station?.routeId ?? null;
  let routeName = station?.routeName ?? null;
  const lastStation =
    typeof lastStationId === "number" && stationById?.has(lastStationId)
      ? stationById.get(lastStationId)
      : null;
  const lastStationRecord =
    lastStationMeta && typeof lastStationMeta === "object"
      ? {
          stationId:
            typeof lastStationMeta.stationId === "number"
              ? lastStationMeta.stationId
              : null,
          stationName:
            typeof lastStationMeta.stationName === "string"
              ? lastStationMeta.stationName
              : null,
          timestamp:
            typeof lastStationMeta.timestamp === "number"
              ? lastStationMeta.timestamp
              : null,
        }
      : null;

  const alignWithLastStation = (fromCandidate, toCandidate) => {
    let resolvedFrom = fromCandidate || null;
    let resolvedTo = toCandidate || null;

    if (
      lastStation &&
      resolvedFrom?.id &&
      resolvedTo?.id &&
      resolvedFrom.id !== resolvedTo.id &&
      lastStation.id === resolvedTo.id &&
      lastStation.id !== resolvedFrom.id
    ) {
      const temp = resolvedFrom;
      resolvedFrom = resolvedTo;
      resolvedTo = temp;
    }

    return { from: resolvedFrom, to: resolvedTo };
  };

  if (station && station.totalStations > 1) {
    progress = Math.round(
      ((station.order - 1) / Math.max(station.totalStations - 1, 1)) * 100
    );
  }

  let nearestSegment = null;

  if (!station) {
    nearestSegment = findNearestSegment(position.lat, position.lng);

    if (nearestSegment) {
      const { from: alignedFrom, to: alignedTo } = alignWithLastStation(
        nearestSegment.from,
        nearestSegment.to
      );
      fromStation = alignedFrom || fromStation;
      toStation = alignedTo || toStation;
      routeId = routeId ?? nearestSegment.routeId;
      routeName = routeName ?? nearestSegment.routeName;
      routeColor = nearestSegment.routeColor ?? routeColor;

      const totalStations = nearestSegment.from?.totalStations;
      if (typeof totalStations === "number" && totalStations > 1) {
        const denominator = Math.max(totalStations - 1, 1);
        const normalized =
          (nearestSegment.from.order - 1 + nearestSegment.ratio) / denominator;
        progress = Math.round(normalized * 100);
      }
    }

    if (!fromStation) {
      const nearestStation = findNearestStation(position.lat, position.lng);
      if (nearestStation?.station) {
        let fromCandidate = nearestStation.station;
        let toCandidate =
          nearestStation.nextStation ||
          nearestStation.previousStation ||
          nearestStation.station;

        if (lastStation) {
          if (lastStation.id === nearestStation.station?.id) {
            fromCandidate = nearestStation.station;
            toCandidate =
              (nearestStation.nextStation &&
              nearestStation.nextStation.id !== lastStation.id
                ? nearestStation.nextStation
                : null) ||
              (nearestStation.previousStation &&
              nearestStation.previousStation.id !== lastStation.id
                ? nearestStation.previousStation
                : null) ||
              nearestStation.station;
          } else if (lastStation.id === nearestStation.previousStation?.id) {
            fromCandidate = nearestStation.previousStation;
            toCandidate = nearestStation.station;
          } else if (lastStation.id === nearestStation.nextStation?.id) {
            fromCandidate = nearestStation.nextStation;
            toCandidate = nearestStation.station;
          }
        }

        const { from: alignedFrom, to: alignedTo } = alignWithLastStation(
          fromCandidate,
          toCandidate
        );
        fromStation = alignedFrom || fromStation;
        toStation = alignedTo || toStation;
        routeId = routeId ?? nearestStation.station.routeId ?? null;
        routeName = routeName ?? nearestStation.station.routeName ?? null;
        routeColor = nearestStation.station.routeColor ?? routeColor;

        const totalStations = nearestStation.station.totalStations;
        if (typeof totalStations === "number" && totalStations > 1) {
          const denominator = Math.max(totalStations - 1, 1);
          const baseIndex = nearestStation.station.order - 1;
          const normalized = baseIndex / denominator;
          progress = Math.round(normalized * 100);
        }
      }
    }
  }

  if (!fromStation && lastStation) {
    fromStation = lastStation;
  }

  if (
    fromStation &&
    (!toStation || toStation.id === fromStation.id || !Number.isFinite(toStation.lat))
  ) {
    const route =
      (fromStation.routeId && routeById.get(fromStation.routeId)) ||
      (routeId && routeById.get(routeId)) ||
      null;

    if (route && Array.isArray(route.stations)) {
      const index = route.stations.findIndex((entry) => entry.id === fromStation.id);

      if (index >= 0) {
        const forward = route.stations[index + 1] || null;
        const backward = route.stations[index - 1] || null;

        if (forward && forward.id !== fromStation.id) {
          toStation = forward;
        } else if (backward && backward.id !== fromStation.id) {
          toStation = backward;
        } else {
          toStation = toStation && toStation.id !== fromStation.id ? toStation : null;
        }
      }
    }
  }

  if (typeof progress === "number") {
    progress = Math.min(100, Math.max(0, progress));
  }

  if (typeof progress !== "number" && fromStation?.totalStations > 1) {
    const denominator = Math.max(fromStation.totalStations - 1, 1);
    progress = Math.round(((fromStation.order - 1) / denominator) * 100);
  }

  if (fromStation?.routeColor) {
    routeColor = fromStation.routeColor;
  }

  const nonStationGeofenceNames = geofences
    .filter((zone) => !zone?.isStation && zone?.name)
    .map((zone) => zone.name);
  const fallbackGeofenceName =
    nonStationGeofenceNames[0] ?? geofences.find((zone) => zone?.name)?.name;

  const lastMessageSource = unit?.lmsg?.t ?? unit?.lmsg?.time ?? unit?.lmsg?.ts;
  const lastMessageAt =
    typeof lastMessageSource === "number"
      ? lastMessageSource * 1000
      : typeof position.timestamp === "number"
      ? position.timestamp
      : Date.now();

  const resolvedRouteId = routeId ?? fromStation?.routeId ?? null;
  const resolvedRouteName =
    routeName ??
    fromStation?.routeName ??
    nearestSegment?.routeName ??
    fallbackGeofenceName ??
    "Unassigned route";

  const lastKnownStationId =
    station?.id ??
    lastStationRecord?.stationId ??
    lastStation?.id ??
    null;
  const lastKnownStationName =
    station?.name ??
    lastStationRecord?.stationName ??
    lastStation?.name ??
    null;
  const lastKnownStationAt = station
    ? lastMessageAt
    : lastStationRecord?.timestamp ?? null;

  return {
    id: unit.id,
    code: unit.nm || `TR-${unit.id}`,
    name: unit.nm || unit.name || `Unit ${unit.id}`,
    routeId: resolvedRouteId,
    routeName: resolvedRouteName,
    stationId: station?.id ?? null,
    stationName:
      station?.name ?? fallbackGeofenceName ?? "Outside monitored geofences",
    lat: position.lat,
    lng: position.lng,
    speed: position.speed,
    lastUpdated: position.timestamp,
    typeId,
    type,
    progress,
    geofences,
    routeColor,
    fromStationId: fromStation?.id ?? null,
    fromStationName: fromStation?.name ?? null,
    toStationId: toStation?.id ?? null,
    toStationName: toStation?.name ?? null,
    isInsideKnownGeofence: Boolean(station),
    lastMessageAt,
    lastKnownStationId,
    lastKnownStationName,
    lastKnownStationAt,
    course: typeof position.course === "number" ? position.course : null,
  };
};

const useNetworkData = ({
  token = process.env.REACT_APP_WIALON_TOKEN,
  resourceId = defaultResourceId,
  baseUrl = process.env.REACT_APP_WIALON_BASE_URL,
  refreshIntervalMs: incomingRefreshInterval,
} = {}) => {
  const normalizedResourceId = resolveNumber(resourceId);
  const [state, setState] = useState({
    routes: staticNetwork.routes,
    trains: staticNetwork.trains,
    totals: staticNetwork.totals,
    trainsByRoute: staticNetwork.trainsByRoute,
    trainTypeTotals: staticNetwork.trainTypeTotals,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [queryParams, setQueryParams] = useState(readQueryParamsFromLocation);

  const updateQueryParams = useCallback(() => {
    const nextParams = readQueryParamsFromLocation();
    setQueryParams((current) => {
      if (
        current.sid === nextParams.sid &&
        current.baseUrl === nextParams.baseUrl
      ) {
        return current;
      }

      return nextParams;
    });
  }, [setQueryParams]);

  const updatedSidRef = useRef(queryParams.sid);
  const baseUrlParamRef = useRef(queryParams.baseUrl);
  const ownsSessionRef = useRef(false);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const firstLoadRef = useRef(true);
  const loadingRef = useRef(false);
  const resourceIdRef = useRef(
    typeof normalizedResourceId === "number" ? normalizedResourceId : null
  );
  const zoneSpecRef = useRef(
    typeof normalizedResourceId === "number"
      ? buildZoneSpec(normalizedResourceId)
      : null
  );
  const allZonesSpecRef = useRef(
    typeof normalizedResourceId === "number"
      ? buildAllZonesSpec(normalizedResourceId)
      : null
  );
  const zoneMetadataRef = useRef({
    resourceId:
      typeof normalizedResourceId === "number" ? normalizedResourceId : null,
    map: new Map(),
    fetched: false,
  });
  const lastStationByTrainRef = useRef(new Map());
  const lastStationMetaByTrainRef = useRef(new Map());

  useEffect(() => {
    const numeric = resolveNumber(resourceId);

    if (typeof numeric === "number") {
      resourceIdRef.current = numeric;
      zoneSpecRef.current = buildZoneSpec(numeric);
      allZonesSpecRef.current = buildAllZonesSpec(numeric);
      zoneMetadataRef.current = {
        resourceId: numeric,
        map: new Map(),
        fetched: false,
      };
      lastStationByTrainRef.current = new Map();
      lastStationMetaByTrainRef.current = new Map();
    } else {
      resourceIdRef.current = null;
      zoneSpecRef.current = null;
      allZonesSpecRef.current = null;
      zoneMetadataRef.current = {
        resourceId: null,
        map: new Map(),
        fetched: false,
      };
      lastStationByTrainRef.current = new Map();
      lastStationMetaByTrainRef.current = new Map();
    }
  }, [resourceId]);

  const resolveBaseUrl = useCallback(() => {
    return baseUrlParamRef.current || baseUrl;
  }, [baseUrl]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleLocationChange = () => {
      updateQueryParams();
    };

    window.addEventListener("popstate", handleLocationChange);
    window.addEventListener("hashchange", handleLocationChange);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      window.removeEventListener("hashchange", handleLocationChange);
    };
  }, [updateQueryParams]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (ownsSessionRef.current && updatedSidRef.current) {
        logout({ sid: updatedSidRef.current, baseUrl: resolveBaseUrl() }).finally(
          () => {
            if (ownsSessionRef.current) {
              updatedSidRef.current = null;
              ownsSessionRef.current = false;
            }
          }
        );
      }
    };
  }, [resolveBaseUrl]);

  const resetToStatic = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }

    lastStationByTrainRef.current = new Map();
    lastStationMetaByTrainRef.current = new Map();

    setState({
      routes: staticNetwork.routes,
      trains: staticNetwork.trains,
      totals: staticNetwork.totals,
      trainsByRoute: staticNetwork.trainsByRoute,
      trainTypeTotals: staticNetwork.trainTypeTotals,
    });
  }, []);

  const syncSidParam = useCallback(
    (sid) => {
      if (typeof window === "undefined") {
        return;
      }

      const params = new URLSearchParams(window.location.search);

      if (!sid) {
        params.delete("sid");
      } else {
        params.set("sid", sid);
      }

      const search = params.toString();
      const hash = window.location.hash || "";
      const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}${
        hash
      }`;
      window.history.replaceState(null, "", newUrl);

      updateQueryParams();
    },
    [updateQueryParams]
  );

  const refresh = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    loadingRef.current = true;
    setError(null);

    const attemptRefresh = async () => {
      const resolvedBaseUrl = resolveBaseUrl();
      let sid = updatedSidRef.current;

      if (!sid) {
        if (!token) {
          throw new Error(
            "Missing Wialon session id (sid) and token. Provide one to enable live data."
          );
        }

        const loginResponse = await loginWithToken({
          token,
          baseUrl: resolvedBaseUrl,
          signal: abortController.signal,
        });

        sid = loginResponse?.eid || loginResponse?.sid;

        if (!sid) {
          throw new Error("Wialon login did not return a session id (sid).");
        }

        updatedSidRef.current = sid;
        ownsSessionRef.current = true;
        syncSidParam(sid);
      }

      const currentResourceId = resourceIdRef.current;

      if (
        typeof currentResourceId === "number" &&
        (!zoneMetadataRef.current?.fetched ||
          zoneMetadataRef.current.resourceId !== currentResourceId)
      ) {
        try {
          const zoneDataResponse = await getZoneData({
            sid,
            baseUrl: resolvedBaseUrl,
            signal: abortController.signal,
            resourceId: currentResourceId,
          });

          const metadataMap = buildZoneMetadataMap(zoneDataResponse);
          zoneMetadataRef.current = {
            resourceId: currentResourceId,
            map: metadataMap,
            fetched: true,
          };
        } catch (zoneDataError) {
          console.warn("Failed to preload zone metadata", zoneDataError);
          zoneMetadataRef.current = {
            resourceId: currentResourceId,
            map: new Map(),
            fetched: true,
          };
        }
      }

      const unitsResponse = await searchUnits({
        sid,
        baseUrl: resolvedBaseUrl,
        signal: abortController.signal,
      });

      const units = unitsResponse?.items || [];
      const tracks = [];

      for (const unit of units) {
        const position = extractPosition(unit);
        if (!position) {
          continue;
        }

        tracks.push({ unit, position });
      }

      // `zoneSpecRef` stores the static portion of the Wialon zone query that we
      // built earlier (see `buildZoneSpec`). It only contains the resource/zone
      // identifiers, so on each refresh we clone it and append the dynamic
      // latitude/longitude for the unit we are checking.
      const zoneSpec = zoneSpecRef.current;
      const allZonesSpec = allZonesSpecRef.current;
      const zoneMetadataById =
        zoneMetadataRef.current && zoneMetadataRef.current.map instanceof Map
          ? zoneMetadataRef.current.map
          : null;
      let batchedGeofences = [];

      if (zoneSpec && tracks.length) {
        const requests = tracks.map(({ position }) => ({
          svc: "resource/get_zones_by_point",
          params: {
            spec: {
              ...zoneSpec,
              lat: position.lat,
              lon: position.lng,
            },
          },
        }));

        try {
          const batchResponse = await batchRequest({
            sid,
            baseUrl: resolvedBaseUrl,
            signal: abortController.signal,
            requests,
          });

          if (Array.isArray(batchResponse)) {
            batchedGeofences = batchResponse;
          } else if (
            batchResponse &&
            typeof batchResponse === "object" &&
            Array.isArray(batchResponse.result)
          ) {
            batchedGeofences = batchResponse.result;
          } else if (
            batchResponse &&
            typeof batchResponse === "object" &&
            Array.isArray(batchResponse.items)
          ) {
            batchedGeofences = batchResponse.items;
          } else {
            batchedGeofences = [];
          }
        } catch (batchError) {
          console.warn(
            "Failed Wialon geofence batch lookup",
            batchError
          );
        }
      }

      const geofenceResults = tracks.map(() => []);
      const fallbackRequests = [];
      const fallbackIndices = [];

      for (let index = 0; index < tracks.length; index += 1) {
        const { unit, position } = tracks[index];
        let geofences = [];

        const previousLastStationId =
          lastStationByTrainRef.current instanceof Map
            ? lastStationByTrainRef.current.get(unit.id) ?? null
            : null;

        const batchedEntry = batchedGeofences?.[index];

        if (batchedEntry && typeof batchedEntry.error === "undefined") {
          const payload =
            batchedEntry &&
            typeof batchedEntry === "object" &&
            !Array.isArray(batchedEntry) &&
            "result" in batchedEntry
              ? batchedEntry.result
              : batchedEntry;

          geofences = normalizeGeofences(
            payload,
            staticNetwork.stationById,
            zoneMetadataById
          );
        } else if (batchedEntry && typeof batchedEntry.error !== "undefined") {
          console.warn(
            "Wialon batch geofence entry failed",
            unit?.id,
            batchedEntry.error
          );
        }

        geofenceResults[index] = geofences;

        const hasStationGeofence = geofences.some((zone) => zone?.isStation);
        const canRequestAllZones =
          allZonesSpec && Number.isFinite(position.lat) && Number.isFinite(position.lng);

        if (!hasStationGeofence && canRequestAllZones) {
          fallbackIndices.push(index);
          fallbackRequests.push({
            svc: "resource/get_zones_by_point",
            params: {
              spec: {
                ...allZonesSpec,
                lat: position.lat,
                lon: position.lng,
              },
            },
          });
        }
      }

      if (fallbackRequests.length) {
        let fallbackResponses = [];

        try {
          const fallbackBatch = await batchRequest({
            sid,
            baseUrl: resolvedBaseUrl,
            signal: abortController.signal,
            requests: fallbackRequests,
          });

          if (Array.isArray(fallbackBatch)) {
            fallbackResponses = fallbackBatch;
          } else if (
            fallbackBatch &&
            typeof fallbackBatch === "object" &&
            Array.isArray(fallbackBatch.result)
          ) {
            fallbackResponses = fallbackBatch.result;
          } else if (
            fallbackBatch &&
            typeof fallbackBatch === "object" &&
            Array.isArray(fallbackBatch.items)
          ) {
            fallbackResponses = fallbackBatch.items;
          } else {
            fallbackResponses = [];
          }
        } catch (fallbackError) {
          console.warn("Failed Wialon all-zones geofence lookup", fallbackError);
        }

        for (let idx = 0; idx < fallbackIndices.length; idx += 1) {
          const trackIndex = fallbackIndices[idx];
          const responseEntry = fallbackResponses?.[idx];

          if (responseEntry && typeof responseEntry.error === "undefined") {
            const payload =
              responseEntry &&
              typeof responseEntry === "object" &&
              !Array.isArray(responseEntry) &&
              "result" in responseEntry
                ? responseEntry.result
                : responseEntry;

            const fallbackGeofences = normalizeGeofences(
              payload,
              staticNetwork.stationById,
              zoneMetadataById
            );

            if (fallbackGeofences.length) {
              geofenceResults[trackIndex] = mergeGeofenceLists(
                geofenceResults[trackIndex],
                fallbackGeofences
              );
            }
          } else if (responseEntry && typeof responseEntry.error !== "undefined") {
            const unitId = tracks[trackIndex]?.unit?.id;
            console.warn(
              "Wialon fallback geofence entry failed",
              unitId,
              responseEntry.error
            );
          }
        }
      }

      const trains = [];

      for (let index = 0; index < tracks.length; index += 1) {
        const { unit, position } = tracks[index];
        const previousLastStationId =
          lastStationByTrainRef.current instanceof Map
            ? lastStationByTrainRef.current.get(unit.id) ?? null
            : null;
        const previousLastStationMeta =
          lastStationMetaByTrainRef.current instanceof Map
            ? lastStationMetaByTrainRef.current.get(unit.id) ?? null
            : null;

        const geofences = geofenceResults[index] || [];

        const train = buildTrainFromUnit({
          unit,
          position,
          geofences,
          stationNameMap: staticNetwork.stationNameMap,
          stationById: staticNetwork.stationById,
          lastStationId: previousLastStationId,
          lastStationMeta: previousLastStationMeta,
        });

        if (train.stationId) {
          lastStationByTrainRef.current.set(train.id, train.stationId);
          lastStationMetaByTrainRef.current.set(train.id, {
            stationId: train.stationId,
            stationName: train.stationName,
            timestamp: train.lastMessageAt,
          });
        } else if (
          typeof previousLastStationId === "number" &&
          Number.isFinite(previousLastStationId)
        ) {
          lastStationByTrainRef.current.set(train.id, previousLastStationId);
          if (previousLastStationMeta) {
            lastStationMetaByTrainRef.current.set(train.id, previousLastStationMeta);
          }
        }

        trains.push(train);
      }

      const aggregates = computeAggregates(staticNetwork.routes, trains);

      if (!mountedRef.current) {
        return;
      }

      setState({
        routes: staticNetwork.routes,
        trains,
        ...aggregates,
      });
      setLastUpdated(new Date());
    };

    try {
      await attemptRefresh();
    } catch (err) {
      let handledError = err;
      const message = err instanceof Error ? err.message : String(err);
      const hadExternalSid =
        Boolean(updatedSidRef.current) && !ownsSessionRef.current;

      if (
        hadExternalSid &&
        (message.includes("error code: 5") || message.includes("error code: 4"))
      ) {
        updatedSidRef.current = null;
        syncSidParam(null);

        try {
          await attemptRefresh();
          return;
        } catch (retryError) {
          handledError = retryError;
        }
      }

      if (!mountedRef.current) {
        return;
      }

      console.error("Failed to refresh Wialon data", handledError);
      setError(
        handledError instanceof Error ? handledError : new Error(String(handledError))
      );
      resetToStatic();
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      loadingRef.current = false;
    }
  }, [token, resetToStatic, syncSidParam, resolveBaseUrl]);

  const renewSessionEffect = useCallback(() => {
    const incomingSid = queryParams.sid || null;
    const incomingBaseUrl = queryParams.baseUrl || null;
    const previousSid = updatedSidRef.current || null;
    const previousBaseUrl = baseUrlParamRef.current || null;

    const sidChanged = incomingSid !== previousSid;
    const baseUrlChanged = incomingBaseUrl !== previousBaseUrl;

    if (!sidChanged && !baseUrlChanged) {
      return;
    }

    if (baseUrlChanged) {
      baseUrlParamRef.current = incomingBaseUrl;
    }

    if (sidChanged) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (ownsSessionRef.current && previousSid) {
        logout({ sid: previousSid, baseUrl: previousBaseUrl || baseUrl }).catch(
          (logoutError) => {
            console.warn("Failed to logout previous Wialon session", logoutError);
          }
        );
      }

      updatedSidRef.current = incomingSid;
      ownsSessionRef.current = false;
    }

    refresh();
  }, [baseUrl, queryParams, refresh]);

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      return;
    }

    renewSessionEffect();
  }, [queryParams, renewSessionEffect]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!token && !queryParams.sid) {
      return undefined;
    }

    const intervalMs =
      typeof incomingRefreshInterval === "number"
        ? incomingRefreshInterval
        : resolveNumber(process.env.REACT_APP_WIALON_POLL_INTERVAL_MS) ||
          DEFAULT_REFRESH_INTERVAL_MS;

    const intervalId = setInterval(() => {
      if (!mountedRef.current || loadingRef.current) {
        return;
      }

      refresh();
    }, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [refresh, token, queryParams.sid, incomingRefreshInterval]);

  const routeOptions = useMemo(
    () =>
      state.routes.map((route) => ({
        value: route.id,
        label: route.name,
      })),
    [state.routes]
  );

  return {
    ...state,
    trainTypeMeta,
    loading,
    error,
    refresh,
    lastUpdated,
    routeOptions,
    isUsingFallback: Boolean(error),
  };
};

export default useNetworkData;
